import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type { Order, OrderStatus } from '@/types/domain';
import { Card } from '@/components/atoms/Card';
import { Badge } from '@/components/atoms/Badge';
import { formatPrice } from '@/utils/price';
import styles from './OrderCard.module.css';

// F-170 B0.6 / B2.4: `order.status_label` is now the canonical UA label
// (computed server-side). We keep this local map as the variant source
// and as a fallback for back-compat when the backend predates B0.6.
const STATUS_LABEL: Record<OrderStatus, { text: string; variant: 'default' | 'accent' | 'success' | 'warning' | 'danger' }> = {
  // F-181: client-initiated, awaiting specialist confirm.
  created: { text: 'Замовлення створено', variant: 'warning' },
  proposed: { text: 'Запропоновано', variant: 'warning' },
  accepted: { text: 'Прийнято', variant: 'accent' },
  in_progress: { text: 'В роботі', variant: 'accent' },
  completed: { text: 'Виконано', variant: 'success' },
  reviewed: { text: 'З відгуком', variant: 'success' },
  cancelled: { text: 'Скасовано', variant: 'danger' },
};

export interface OrderCardProps {
  order: Order;
  asLink?: boolean;
}

export function OrderCard({ order, asLink = true }: OrderCardProps): JSX.Element {
  const fallback = STATUS_LABEL[order.status];
  const text = order.status_label || fallback.text;
  const content = (
    <Card interactive={asLink}>
      <div className={styles.card}>
        <div className={styles.row}>
          <span className={styles.title}>{order.title}</span>
          {/* F-170 B3.3: route every price render through `formatPrice`.
              `Order` itself doesn't carry `price_is_from` (the order
              snapshot has a concrete final figure), so the prefix is
              never added here — the helper still owns the EUR format. */}
          <span className={styles.price}>{formatPrice(order.price_eur)}</span>
        </div>
        <div className={styles.statusRow}>
          <Badge variant={fallback.variant}>{text}</Badge>
          {order.scheduled_at ? (
            <span className={styles.meta}>
              {dayjs(order.scheduled_at).format('D MMM, HH:mm')}
            </span>
          ) : null}
          {order.duration_minutes ? (
            <span className={styles.meta}>
              {/* F-160 (C8): only emit the leading `·` when there's a
                  scheduled_at sibling on the left, otherwise the dot
                  hangs in front of the duration. */}
              {order.scheduled_at ? '· ' : ''}
              {order.duration_minutes} хв
            </span>
          ) : null}
        </div>
        {order.description ? <div className={styles.meta}>{order.description}</div> : null}
      </div>
    </Card>
  );
  if (!asLink) return content;
  return (
    <Link to={`/orders/${order.id}`} style={{ display: 'block', textDecoration: 'none' }}>
      {content}
    </Link>
  );
}
